// src/pages/ResetPassword.js
//
// Paso 2 del reset: usuario llega desde el link del correo
//   http://localhost:3000/reset-password?userId=xxx&code=yyy
// Le pedimos la contraseña nueva y llamamos POST /confirmar-reset.

import React, { useState, useMemo } from 'react';
import {
  Box, Container, Card, CardContent, TextField, Button, Typography, Stack,
  Alert, IconButton, InputAdornment,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import VisibilityOffIcon  from '@mui/icons-material/VisibilityOff';

import Logo from '../components/Logo';
import { confirmarReset } from '../api/authApi';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const userId = useMemo(() => params.get('userId') || '', [params]);
  const code   = useMemo(() => params.get('code')   || '', [params]);

  const [pass, setPass]           = useState('');
  const [repite, setRepite]       = useState('');
  const [mostrar, setMostrar]     = useState(false);
  const [cargando, setCargando]   = useState(false);
  const [error, setError]         = useState('');
  const [exito, setExito]         = useState(false);

  const linkInvalido = !userId || !code;

  const enviar = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (pass.length < 6)        { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    if (pass !== repite)        { setError('Las contraseñas no coinciden.'); return; }

    setCargando(true);
    try {
      await confirmarReset(userId, code, pass);
      setExito(true);
    } catch (err) {
      setError(err.mensajeUsuario || 'El enlace es inválido o expiró. Solicita uno nuevo.');
    } finally {
      setCargando(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FBF7F0', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ py: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/login')} aria-label="Volver al login">
          <ArrowBackIcon />
        </IconButton>
        <Logo />
      </Container>

      <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', py: 4 }}>
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="overline" color="text.secondary">Nueva contraseña</Typography>
            <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mb: 1 }}>
              Crea tu contraseña
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Elige una contraseña que puedas recordar.
            </Typography>

            {linkInvalido && (
              <Alert severity="error" sx={{ mb: 2 }}>
                El enlace no es válido. Vuelve a solicitar el reset desde la página de login.
              </Alert>
            )}

            {exito ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Tu contraseña se actualizó. Ya puedes iniciar sesión.
                </Alert>
                <Button variant="contained" fullWidth onClick={() => navigate('/login', { replace: true })}>
                  Ir al login
                </Button>
              </>
            ) : (
              <form onSubmit={enviar}>
                <Stack spacing={2.5}>
                  <TextField
                    label="Nueva contraseña"
                    type={mostrar ? 'text' : 'password'}
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    disabled={linkInvalido}
                    autoFocus
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setMostrar((v) => !v)} edge="end" size="small">
                            {mostrar ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Repite la contraseña"
                    type={mostrar ? 'text' : 'password'}
                    value={repite}
                    onChange={(e) => setRepite(e.target.value)}
                    disabled={linkInvalido}
                    fullWidth
                  />
                  {error && <Alert severity="error">{error}</Alert>}
                  <Button type="submit" variant="contained" size="large" fullWidth
                          disabled={cargando || linkInvalido}>
                    {cargando ? 'Guardando…' : 'Guardar contraseña'}
                  </Button>
                </Stack>
              </form>
            )}
          </CardContent>
        </Card>
      </Container>

      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="caption" color="text.secondary">
          HolaVeci · Redeprog SAS
        </Typography>
      </Box>
    </Box>
  );
}
